import React, { useRef, useEffect } from 'react';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import WaterDropRoundedIcon from '@mui/icons-material/WaterDropRounded';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { v3Theme } from '../theme';
import { useTranslation } from 'react-i18next';

// Image paths - using public folder references
const userpic = '/img/userpic.png';
const health = '/img/health.webp';
const steps = '/img/steps.webp';

/**
 * Game header with user profile, status indicators, and burger menu.
 *
 * @param {Object} props
 * @param {Object} props.profile - User profile data from v3State
 * @param {boolean} props.connectionState - SSE connection state
 * @param {number} props.healthPercent - Health as 0-1 fraction
 * @param {number} props.manaPercent - Mana as 0-1 fraction
 * @param {number} props.stepsPercent - Steps as 0-1 fraction
 * @param {number} props.staminaPercent - Stamina bar width as 0-100 percentage
 * @param {number} props.staminaRegenPercent - Stamina regen bar width as 0-100 percentage
 * @param {number} props.currentStamina - Current stamina value
 * @param {boolean} props.isMenuOpen - Whether the burger menu dropdown is open
 * @param {Function} props.onBurgerClick - Toggle burger menu
 * @param {Function} props.onCloseMenu - Close burger menu (used for click-outside)
 * @param {Function} props.onSettings - Open settings
 * @param {Function} props.onMarketplace - Open marketplace
 * @param {Function} props.onAdministration - Open administration panel
 */
function V3Header({
    profile,
    connectionState,
    healthPercent,
    manaPercent,
    stepsPercent,
    staminaPercent,
    staminaRegenPercent,
    currentStamina,
    isMenuOpen,
    onBurgerClick,
    onCloseMenu,
    onSettings,
    onMarketplace,
    onAdministration,
}) {
    const { t } = useTranslation();
    const menuRef = useRef(null);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                onCloseMenu();
            }
        };

        if (isMenuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('touchstart', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [isMenuOpen, onCloseMenu]);

    return (
        <header className="game-header">
            {/* Left section - 40% - User profile */}
            <div className="header-left">
                <div className="user-profile">
                    <table className="profile-table">
                        <tbody>
                            <tr>
                                <td className="avatar-cell">
                                    <div className="avatar-wrapper">
                                        <div className="avatar">
                                            <img src={userpic} alt="User Avatar" />
                                        </div>
                                    </div>
                                </td>
                                <td className="info-cell">
                                    <div className="main-progress">
                                        <div className="progress-fill" style={{width: `${profile.expPercent || 0}%`}}></div>
                                        <span className="progress-text">{profile.exp}/{profile.maxExp}</span>
                                    </div>
                                    <div className="secondary-bar-container">
                                        {currentStamina > 0 ? (
                                            <div className="secondary-bar" style={{width: `${staminaPercent}%`}}></div>
                                        ) : (
                                            <div className="secondary-bar-regen" style={{width: `${staminaRegenPercent}%`}}></div>
                                        )}
                                    </div>
                                    <div className="bottom-row">
                                        <div
                                            className="level-badge"
                                            style={{
                                                backgroundColor: connectionState ? 'var(--v3-main)' : '#e74c3c'
                                            }}
                                            title={connectionState ? 'SSE Connected' : 'SSE Disconnected'}
                                        >
                                            {profile.level}
                                        </div>
                                        <div className="username">{profile.username}</div>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Center section - 40% - Status indicators */}
            <div className="header-center">
                <div className="status-indicators">
                    <div className="circular-indicator health-indicator">
                        <div className="circular-progress">
                            <svg className="progress-ring" width="32" height="32">
                                <circle className="progress-ring-bg" cx="16" cy="16" r="13" strokeWidth="2.5"></circle>
                                <circle className="progress-ring-fill" cx="16" cy="16" r="13" strokeWidth="2.5"
                                        strokeDasharray="81.7" strokeDashoffset={81.7 * (1 - healthPercent)}></circle>
                                <circle className="progress-ring-inner" cx="16" cy="16" r="12" fill="#f5f6fa"></circle>
                            </svg>
                            <div className="circular-content">
                                <img src={health} alt="Health" className="circular-icon health-icon" />
                            </div>
                        </div>
                        <span className="circular-value">{Math.round(healthPercent * 100)}%</span>
                    </div>
                    <div className="circular-indicator mana-indicator">
                        <div className="circular-progress">
                            <svg className="progress-ring" width="32" height="32">
                                <circle className="progress-ring-bg" cx="16" cy="16" r="13" strokeWidth="2.5"></circle>
                                <circle className="progress-ring-fill" cx="16" cy="16" r="13" strokeWidth="2.5"
                                        strokeDasharray="81.7" strokeDashoffset={81.7 * (1 - manaPercent)}></circle>
                                <circle className="progress-ring-inner" cx="16" cy="16" r="12" fill="#f5f6fa"></circle>
                            </svg>
                            <div className="circular-content">
                                <WaterDropRoundedIcon
                                    className="circular-icon"
                                    style={{
                                        color: v3Theme.main,
                                        fontSize: '16px'
                                    }}
                                />
                            </div>
                        </div>
                        <span className="circular-value">{Math.round(manaPercent * 100)}%</span>
                    </div>
                    <div className="circular-indicator steps-indicator">
                        <div className="circular-progress">
                            <svg className="progress-ring" width="32" height="32">
                                <circle className="progress-ring-bg" cx="16" cy="16" r="13" strokeWidth="2.5"></circle>
                                <circle className="progress-ring-fill" cx="16" cy="16" r="13" strokeWidth="2.5"
                                        strokeDasharray="81.7" strokeDashoffset={81.7 * (1 - stepsPercent)}></circle>
                                <circle className="progress-ring-inner" cx="16" cy="16" r="12" fill="#f5f6fa"></circle>
                            </svg>
                            <div className="circular-content">
                                <img src={steps} alt="Steps" className="circular-icon" />
                            </div>
                        </div>
                        <span className="circular-value">{profile.steps ?? 0}/{profile.maxSteps ?? 0}</span>
                    </div>
                </div>
            </div>

            {/* Right section - 20% - Menu button */}
            <div className="header-right">
                <div ref={menuRef} className="menu-container">
                    <button className="burger-menu" onClick={onBurgerClick}>
                        <span className="burger-line"></span>
                        <span className="burger-line"></span>
                        <span className="burger-line"></span>
                    </button>
                    {isMenuOpen && (
                        <div className="dropdown-menu">
                            <button className="menu-item" onClick={onMarketplace}>
                                <img
                                    src="/img/exchange.webp"
                                    className="menu-icon"
                                    alt="Marketplace"
                                    style={{
                                        width: '24px',
                                        height: '24px',
                                        filter: 'brightness(0) saturate(100%) invert(42%) sepia(0%) saturate(3013%) hue-rotate(233deg) brightness(104%) contrast(90%)'
                                    }}
                                />
                                <span>{t('navigation.marketplace')}</span>
                            </button>
                            <button className="menu-item" onClick={onSettings}>
                                <SettingsRoundedIcon className="menu-icon" />
                                <span>{t('navigation.settings')}</span>
                            </button>
                            {profile.isGM && (
                                <button className="menu-item" onClick={onAdministration}>
                                    <AdminPanelSettingsIcon className="menu-icon" />
                                    <span>{t('navigation.administration')}</span>
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
}

export default V3Header;
